import React from "react";
import { Card, CardHeader, CardBody, CardTitle, Button } from "reactstrap";
import { connect } from "react-redux";
import ReactTable from "react-table";
import TablePagination from "../../components/Table/TablePagination";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import Constants from "../../core/Constants";
import loadingAction from "../../core/redux/actions/loadingAction";
import strings from "../../core/translate";
import RowActions, {
  editAction,
  deleteAction,
} from "../../components/Table/RowActions";
import { Scopes } from "../../shared/scopes.enum";
import { FormModal } from "../../components/Form/FormKit";
import BranchPhotoForm from "./BranchPhotoForm";

/**
 * The photos of one garage: the picture, when it was added and what you can do
 * with it. The garage is already known from wherever this panel is hosted
 * (garage setup, or the standalone photos page), so the merchant and the branch
 * are not repeated as columns.
 */
class BranchPhotosPanel extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      dataSource: [],
      formOpen: false,
      editing: null,
      canWrite: this.hasPermission([Scopes.MERCHANTS_BRANCHES_PHOTOS_WRITE]),
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.callPage();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.branchId !== this.props.branchId) {
      this.callPage();
    }
  }

  get pathname() {
    return `merchant-branch/${this.props.branchId}/photos`;
  }

  callPage = async () => {
    const { branchId } = this.props;
    if (!branchId || branchId === ":selectedBranch") {
      this.setState({ dataSource: [] });
      return;
    }

    this.setState({ isLoading: true });
    const response = await this.engine.getItem(this.pathname);
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        dataSource: response.data.items.map((item, key) => ({
          id: key,
          data: item,
          servicePhoto:
            item.url && item.url.length > 1 ? (
              // the api stores the served path (uploads/branch-images/<file>)
              <img
                key={key}
                alt={strings.garage}
                src={`${Constants.serverlink}${item.url}`}
                className={"imgsponser"}
              />
            ) : (
              "-"
            ),
          created: new Date(item.createdAt),
          actions: this.handleActions(key, item),
        })),
      });
    } else {
      this.setState({ isLoading: false });
    }
    this.props.loadingAction(false);
  };

  handleActions = (index, item) => (
    <RowActions
      actions={[
        editAction(() => this.handleEdit(index), this.state.canWrite),
        deleteAction(() => this.handleDelete(item.id), this.state.canWrite),
      ]}
    />
  );

  handleDelete = async (valueid) => {
    if (window.confirm(strings.confirmdelete)) {
      this.props.loadingAction(true);
      const result = await this.engine.deleteItem(this.pathname, valueid);
      if (result) {
        this.callPage();
      }
      this.props.loadingAction(false);
    }
  };

  handleAdd = () => this.setState({ formOpen: true, editing: null });

  handleEdit = (index) =>
    this.setState({ formOpen: true, editing: this.state.dataSource[index].data });

  handleCloseForm = () => this.setState({ formOpen: false, editing: null });

  handleSaved = () =>
    this.setState({ formOpen: false, editing: null }, this.callPage);

  render() {
    const { branchId, branchName, title, children } = this.props;
    const { dataSource, isLoading, formOpen, editing, canWrite } = this.state;

    const columns = [
      {
        Header: `${strings.service} ${strings.picture}`,
        accessor: "servicePhoto",
        sortable: false,
      },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => (row.value ? row.value.toLocaleDateString() : "-"),
      },
      {
        Header: strings.actions,
        accessor: "actions",
        sortable: false,
      },
    ];

    return (
      <Card>
        <CardHeader>
          <CardTitle tag="h4" className="pull-left">
            {title !== undefined ? title : strings.photos}
          </CardTitle>
          {canWrite && branchId && (
            <Button
              color="primary"
              className="pull-right"
              onClick={this.handleAdd}
            >
              {strings.add}
            </Button>
          )}
          {children}
        </CardHeader>
        <CardBody>
          <ReactTable
            previousText={strings.previous}
            nextText={strings.next}
            loadingText={strings.loading}
            noDataText={strings.nodata}
            pageText={strings.page}
            data={dataSource}
            loading={isLoading}
            columns={columns}
            defaultPageSize={10}
            showPaginationTop={false}
            showPaginationBottom
            PaginationComponent={TablePagination}
            className="-striped -highlight primary-pagination"
          />
        </CardBody>

        <FormModal
          isOpen={formOpen}
          onClose={this.handleCloseForm}
          icon="nc-icon nc-album-2"
          size="md"
          title={
            editing
              ? `${strings.edit} ${strings.picture}`
              : `${strings.add} ${strings.photos}`
          }
          subtitle={branchName}
        >
          <BranchPhotoForm
            key={(editing && editing.id) || "new"}
            item={editing}
            branchId={branchId}
            onSaved={this.handleSaved}
            onCancel={this.handleCloseForm}
          />
        </FormModal>
      </Card>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(BranchPhotosPanel);
